import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { useUpdateMeMutation } from "@/pages/Private/redux/account/account.api";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { UpdateLinkedinLimits, UpdateLinkedinLimitsSchema } from "../schema/account";

interface LinkedinSettingsModalProps {
	isVisible: boolean;
	visitsPerDay: number;
	connectionRequestsPerDay: number;
	messagesPerDay: number;
	handleClose: () => void;
}

/**
 * Self-service "Edit LinkedIn settings" modal — lets the current user tune their own daily
 * LinkedIn automation limits via the self `updateMe` (PATCH /users) endpoint. Mirrors the
 * admin EditLinkedinSettings panel but scoped to the signed-in user (no admin permission).
 */
export const LinkedinSettingsModal: FunctionComponent<LinkedinSettingsModalProps> = ({
	isVisible,
	visitsPerDay,
	connectionRequestsPerDay,
	messagesPerDay,
	handleClose,
}) => {
	const { t } = useTranslation();

	const [updateMe, { isLoading, error }] = useUpdateMeMutation();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<UpdateLinkedinLimits>({
		defaultValues: { visitsPerDay, connectionRequestsPerDay, messagesPerDay },
		resolver: zodResolver(UpdateLinkedinLimitsSchema),
	});

	const formErrors = Object.values(errors).map((err) => err?.message) as ERROR_TYPE[];

	const handleCloseModal = () => {
		reset({ visitsPerDay, connectionRequestsPerDay, messagesPerDay });
		handleClose();
	};

	const onSubmit = async (values: UpdateLinkedinLimits) => {
		try {
			await updateMe({
				visitsPerDay: values.visitsPerDay,
				connectionRequestsPerDay: values.connectionRequestsPerDay,
				messagesPerDay: values.messagesPerDay,
			}).unwrap();
			handleCloseModal();
		} catch (err) {
			console.error(err);
		}
	};

	const fields: { name: keyof UpdateLinkedinLimits; label: string }[] = [
		{ name: "visitsPerDay", label: t("basics.visitsPerDay") },
		{ name: "connectionRequestsPerDay", label: t("basics.connectionRequestsPerDay") },
		{ name: "messagesPerDay", label: t("basics.messagesPerDay") },
	];

	return (
		<Modal
			handleClose={handleCloseModal}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isVisible}
			submitButtonText={t("basics.saveChanges")}
			title={t("account.connectedAccounts.editLinkedinSettings")}
		>
			<div className="mt-2 flex flex-col gap-2">
				{fields.map((field) => (
					<Controller
						key={field.name}
						control={control}
						name={field.name}
						render={({ field: rhfField }) => (
							<InputField
								error={errors[field.name]?.message}
								label={field.label}
								min={0}
								name={field.name}
								type="number"
								value={rhfField.value ?? 0}
								handleChange={(e) => rhfField.onChange(Number(e.target.value))}
							/>
						)}
					/>
				))}

				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</div>
		</Modal>
	);
};
